/**
 * Per-account feature entitlements. Pricing is handled case-by-case, so features
 * are gated by per-account flags an operator flips — not by a fixed plan. The
 * canonical list lives here so the admin UI, the API, the app, and enforcement
 * all agree on the same keys + defaults.
 *
 * `workspaces` is backed by the existing `accounts.workspaces_enabled` column
 * (single source of truth); every other flag is stored in `accounts.entitlements`
 * JSON. All default OFF — turn them on per account when you sell them.
 */
export interface FeatureDef {
  /** Human label for the admin toggle. */
  label: string;
  /** Default when unset (all gated features default off). */
  default: boolean;
  /** Backed by a dedicated column instead of the JSON blob. */
  column?: 'workspacesEnabled';
  /** Not built yet — shown in admin as "roadmap" so it isn't toggled on by mistake. */
  roadmap?: boolean;
}

export const FEATURES = {
  workspaces: {
    label: 'Workspaces (parent / child agencies)',
    default: false,
    column: 'workspacesEnabled',
  },
  // Default ON (2026-07-09, George): every account can bring its own AI key —
  // the assistant is frontier-quality either way; BYOK is control, not a gate.
  byok: { label: 'Bring-your-own AI key (BYOK)', default: true },
  api: { label: 'API access (issue account tokens)', default: false },
  sso: { label: 'SSO / SAML', default: false, roadmap: true },
  rbac: { label: 'Role-based access control', default: false, roadmap: true },
  whiteLabel: { label: 'White-label branding', default: false, roadmap: true },
  downline: { label: 'Downline / override hierarchy', default: false, roadmap: true },
  disputes: { label: 'Dispute / recovery workflow', default: false },
  // Live: SFTPGo host (Fly.io) writes uploads straight to R2; the cron sweep ingests
  // them. Per-account users are auto-provisioned when this is toggled on.
  sftp: { label: 'SFTP statement ingestion', default: false },
  // Email-in: carriers email statements to a per-carrier inbound address; Resend's
  // inbound webhook feeds them into ingest. Gates alias management (app + admin).
  emailIngest: { label: 'Email statement ingestion', default: false },
  // Book-intelligence extras — built but NICHE/data-dependent, so gated until proven
  // per account. Medicare lifecycle (RDE radar + renewal forecast) is MA/PDP-only and
  // needs term dates; 1099 reconciliation only holds up with a complete year loaded.
  medicareLifecycle: {
    label: 'Medicare lifecycle (RDE radar + renewal forecast)',
    default: false,
  },
  tax1099: { label: 'Year-end 1099 reconciliation', default: false },
  // ACA / major-medical book-value forecast (annual recurring commission, attrition-
  // adjusted) + enrollment-cohort retention. Gated until validated per account.
  bookValue: { label: 'Book-value forecast & cohort retention', default: false },
  // All-time chargeback proof-out: every clawback across the loaded history traced to
  // its original payment (or flagged never-paid), with per-policy paid-vs-clawed
  // ledger totals and a carrier-ready CSV export. App-only (off the SDK).
  chargebackProof: { label: 'Chargeback proof-out (all-time trace + export)', default: false },
  // Payment gaps — the unpaid mirror of the proof-out: months with no record at all
  // between two paid months on the same policy, classified by whether the carrier
  // ever caught the money up, with a carrier-ready CSV export. App-only (off the SDK).
  paymentGaps: { label: 'Payment gaps (missing-month detection + export)', default: false },
} as const satisfies Record<string, FeatureDef>;

export type FeatureKey = keyof typeof FEATURES;
export type Entitlements = Record<FeatureKey, boolean>;

export const FEATURE_KEYS = Object.keys(FEATURES) as FeatureKey[];

/**
 * Resolve an account's effective entitlements: defaults, overlaid with the stored
 * JSON flags, with `workspaces` always sourced from its own column.
 */
export function resolveEntitlements(
  stored: Record<string, unknown> | null | undefined,
  workspacesEnabled: boolean,
): Entitlements {
  const out = {} as Entitlements;
  for (const key of FEATURE_KEYS) out[key] = FEATURES[key].default;
  if (stored) {
    for (const key of FEATURE_KEYS) {
      if (typeof stored[key] === 'boolean') out[key] = stored[key] as boolean;
    }
  }
  out.workspaces = workspacesEnabled; // dedicated column wins
  return out;
}

/**
 * Apply one toggle to the stored-JSON blob (excludes `workspaces`, which the
 * caller writes to its own column). Returns the new JSON object to persist.
 */
export function withEntitlement(
  stored: Record<string, unknown> | null | undefined,
  key: FeatureKey,
  value: boolean,
): Record<string, boolean> {
  const next: Record<string, boolean> = {};
  for (const k of FEATURE_KEYS) {
    if (k === 'workspaces') continue;
    const cur =
      stored && typeof stored[k] === 'boolean' ? (stored[k] as boolean) : FEATURES[k].default;
    next[k] = cur;
  }
  if (key !== 'workspaces') next[key] = value;
  return next;
}

export function isFeatureKey(v: unknown): v is FeatureKey {
  return typeof v === 'string' && (FEATURE_KEYS as string[]).includes(v);
}
